/**
 * Signals API Service
 * Handles all signals-related API calls to the backend
 */

import { authenticatedFetch } from "./apiClient";
import { throwIfNotOk } from "../utils/apiRequestError";
import type { SystemPerformanceData } from "../types/signals";
import type { BackendTrade } from "./tradeApi";

export interface SignalStatusResponse {
  signals_enabled: boolean;
  enabled_until: string | null; // ISO 8601 timestamp
  time_remaining_seconds: number | null; // Calculated: enabled_until - now()
  websocket_path?: string | null; // e.g. "/ws/signals" (provided by enable endpoint)
}

/**
 * Enable signals for the current user
 * Sets signals_enabled=true and enabled_until=now()+3hours in user_signal_preferences
 */
export const enableSignals = async (): Promise<SignalStatusResponse> => {
  const response = await authenticatedFetch("/v1/signals/enable", {
    method: "POST",
  });

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to enable signals: ${response.status}`);
  }

  return await response.json();
};

/**
 * Disable signals for the current user
 * Sets signals_enabled=false and clears enabled_until
 */
export const disableSignals = async (): Promise<SignalStatusResponse> => {
  const response = await authenticatedFetch("/v1/signals/disable", {
    method: "POST",
  });

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to disable signals: ${response.status}`);
  }

  return await response.json();
};

/**
 * Get current signals status for the current user
 * Returns signals_enabled, enabled_until, and time_remaining_seconds
 */
export const getSignalsStatus = async (): Promise<SignalStatusResponse> => {
  const response = await authenticatedFetch("/v1/signals/status");

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to get signals status: ${response.status}`);
  }

  return await response.json();
};

/**
 * Get system performance stats (GET /system-performance).
 * Same shape as WebSocket arbiter_performance.data. Call when Tradar loads; WebSocket updates when setups enabled.
 */
export const getSystemPerformance = async (): Promise<SystemPerformanceData> => {
  const response = await authenticatedFetch("/system-performance");

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to get system performance: ${response.status}`);
  }

  return await response.json();
};

/**
 * Execute a signal trade (Take Trade)
 * POST /api/signals/{signal_id}/execute
 */
export const executeSignalTrade = async (
  signalId: string,
  body: {
    lot_size: number;
    broker_account_code: string;
    stop_loss_price?: number;
    take_profit_price?: number;
  }
): Promise<{ trade: BackendTrade; order_result: any }> => {
  const response = await authenticatedFetch(`/signals/${signalId}/execute`, {
    method: "POST",
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to execute signal trade: ${response.status}`);
  }

  return await response.json();
};

/**
 * Calculate signal risk (RR, loss/profit potentials) without executing
 * POST /api/signals/{signal_id}/calculate-risk
 */
export const calculateSignalRisk = async (
  signalId: string,
  body: {
    lot_size: number;
    stop_loss_price?: number;
    take_profit_price?: number;
    risk_reward_ratio?: number;
    account_equity?: number;
  }
): Promise<{
  sl_pips: number;
  tp_pips: number;
  risk_reward_ratio: number;
  risk_amount: number;
  profit_amount: number;
  risk_pct?: number;
  profit_pct?: number;
  lot_size: number;
  stop_loss_price: number;
  take_profit_price: number;
}> => {
  const response = await authenticatedFetch(`/signals/${signalId}/calculate-risk`, {
    method: "POST",
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    await throwIfNotOk(response, `Failed to calculate signal risk: ${response.status}`);
  }

  return await response.json();
};
